import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import iconClose from 'src/assets/close.svg';
import iconSearch from 'src/assets/search.svg';
import iconCheck from 'src/assets/checked_primary2.svg';
import TextInput, { useTextInputValue } from 'src/components/TextInput';
import './style.scoped.css';
import BottomSheetService from 'src/utils/BottomSheetService';
import Item from 'src/models/Item';
import { removeAccent } from 'src/utils';

interface NationItemProps {
  id: string;
  name: string;
  checked?: boolean;
  onClick?: (id: string) => void;
  type?: 'header' | 'item';
}
const NationItem = React.memo((props: NationItemProps) => {
  const { id, name, checked = false, onClick, type } = props;
  const _handleClick = useCallback(() => {
    if (onClick) {
      onClick(id);
    }
  }, [onClick, id]);
  // console.log('Render NationItem');
  if (type == 'header') {
    return (
      <div className="px-4 bg-primaryLight2">
        <div className="py-1">
          <div className="bodyText font-semibold text-onSurface">{name}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="px-4" onClick={_handleClick}>
      {checked ? (
        <div className="py-3 border-b border-line2 flex flex-row">
          <div className="bodyText font-semibold text-primary flex-1">
            {name}
          </div>
          <img src={iconCheck} className="w-6 h-6" />
        </div>
      ) : (
        <div className="py-3 border-b border-line2">
          <div className="bodyText font-normal text-onSurface">{name}</div>
        </div>
      )}
    </div>
  );
});

interface NationListProps {
  nationList: Item[];
  value: string;
  onChange: (id: string) => void;
}
const NationBottomSheet = (props: NationListProps) => {
  const { t } = useTranslation();
  const { value, onChange } = props;
  const [keyword, setKeyword] = useState('');
  const { nationList } = props;
  const nationListDisplay = useMemo(() => {
    const sortedNationList =
      keyword && keyword.trim() != ''
        ? nationList
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .filter(
              (item) =>
                removeAccent(item.name.toLocaleLowerCase()).indexOf(
                  removeAccent(keyword.toLowerCase())
                ) > -1
            )
        : nationList.sort((a, b) => (a.name > b.name ? 1 : -1));
    const result: NationItemProps[] = [];
    let currentLetter: string = null;
    for (const nation of sortedNationList) {
      const letterOfNation = nation.name[0];
      if (!currentLetter || currentLetter != letterOfNation) {
        currentLetter = letterOfNation;
        result.push({
          id: currentLetter,
          name: currentLetter,
          type: 'header',
        });
      }
      result.push({
        id: nation.id,
        name: nation.name,
        type: 'item',
      });
    }
    return result;
  }, [nationList, keyword]);
  // console.log('nationListDisplay', nationListDisplay);
  const _handleCloseBottomSheet = () => {
    BottomSheetService.instance.current.close();
  };

  const _handleClickItem = useCallback(
    (id: string) => {
      onChange(id);
      BottomSheetService.instance.current.close();
    },
    [onChange]
  );

  const _handleChangeKeyword = (event) => {
    setKeyword(event.target.value);
  };

  return (
    <div className="bottomSheetContent">
      <div className="bottomSheetBar">
        <div className="bottomSheetTitleContainer">
          <div className="flex-1 bottomSheetTitle">
            {t('choose_nationality')}
          </div>
        </div>
        <div onClick={_handleCloseBottomSheet}>
          <img src={iconClose} className="w-6 h-6" />
        </div>
      </div>
      <div className="flex items-center py-3 px-4 bg-surface">
        <TextInput
          containerClassName={'mr-2 flex-1'}
          rightComponent={<img src={iconSearch} className="w-6 h-6" />}
          placeholder={t('search')}
          value={keyword}
          onChange={_handleChangeKeyword}
          useDisabledStyle={true}
        />
      </div>
      <div className="bottomSheetScroll">
        {nationListDisplay.map((item) => {
          return (
            <NationItem
              id={item.id}
              name={item.name}
              key={item.id}
              checked={item.id == value}
              onClick={_handleClickItem}
              type={item.type}
            />
          );
        })}
      </div>
    </div>
  );
};

export default NationBottomSheet;
